import { useEffect, useRef } from "react";
import { useGetLmsLaunchSyncStatusQuery, LmsLaunchSyncStatus } from "@axio/graphql/generated/graphql";

type UseLmsLaunchSyncStatusProps = {
  jobId: string;
  redirectTo: string;
  onComplete: (redirectTo: string) => void;
  onFailed: () => void;
};

type UseLmsLaunchSyncStatusResult = {
  status: LmsLaunchSyncStatus | undefined;
  failedReason: string | undefined;
  isLoading: boolean;
  error: Error | undefined;
};

export function useLmsLaunchSyncStatus({
  jobId,
  redirectTo,
  onComplete,
  onFailed,
}: UseLmsLaunchSyncStatusProps): UseLmsLaunchSyncStatusResult {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;

  const { data, loading, error, stopPolling } = useGetLmsLaunchSyncStatusQuery({
    variables: { jobId },
    skip: !jobId,
    pollInterval: 2000,
    fetchPolicy: "network-only",
  });

  const status = data?.getLmsLaunchSyncStatus?.status;
  const failedReason = data?.getLmsLaunchSyncStatus?.failedReason ?? undefined;

  useEffect(() => {
    if (status === LmsLaunchSyncStatus.Completed) {
      stopPolling();
      onCompleteRef.current(redirectTo);
    } else if (status === LmsLaunchSyncStatus.Failed) {
      stopPolling();
      onFailedRef.current();
    }
  }, [status, redirectTo, stopPolling]);

  return {
    status,
    failedReason,
    isLoading: loading,
    error: error as Error | undefined,
  };
}
